import { useTranslation } from 'react-i18next';
import { PaymentMethodForm } from './PaymentMethodForm';
import { TabGroup } from '../common/Tabs/TabGroup';
import { Tab } from '../common/Tabs/Tab';
import { Row } from '../common/Row';
import { ErrorMessage } from '../ErrorMessage';
import { Country } from '../../types/common';
import {
  CollectMethodPaymentType,
  GroupedCollectMethods,
} from '../../types/CollectMethod';
import { AccountBrandingSettings } from '../../types/Account';
import { CardProcessorLogo } from './CardProcessorLogo';
import { NewApiError } from '../../types/NewApiError';
import { callIfFunction } from '../../utils/utils';
import { useUrlState } from '../../hooks/useUrlState';

export interface PaymentMethodsProps {
  processing: boolean;
  onNext: (data: Metadata) => void;
  onCancel?: VoidFunction;
  onTabClick?: (
    from: CollectMethodPaymentType,
    to: CollectMethodPaymentType
  ) => void;
  submitText?: string;
  cancelText?: string;
  country: Country;
  groupedCollectMethods: GroupedCollectMethods;
  // This is used to show the collect methods for the plan,
  // when the invoice is from a subscription
  planCollectMethods?: GroupedCollectMethods;
  isUniquePayment?: boolean;
  brandingSettings?: AccountBrandingSettings;
  errorObj: NewApiError | null;
  pagoticId?: string;
  defaultPaymentType?: CollectMethodPaymentType;
}

const tabOrder: CollectMethodPaymentType[] = [
  'card',
  'cbu',
  'debin',
  'qr',
  'ticket',
  'transfer',
];

export function PaymentMethods({
  onNext,
  onCancel,
  onTabClick,
  defaultPaymentType,
  processing,
  submitText,
  cancelText,
  country,
  groupedCollectMethods,
  planCollectMethods,
  isUniquePayment,
  brandingSettings,
  errorObj,
  pagoticId,
}: PaymentMethodsProps): JSX.Element {
  const { t } = useTranslation();

  submitText = submitText ? submitText : t('common.continue');

  const planCM = planCollectMethods
    ? Object.values(planCollectMethods)
        .flat(1)
        .map((cm) => cm.id)
    : [];

  const paymentTypes = tabOrder.filter(
    (paymentType) => groupedCollectMethods[paymentType]?.length > 0
  );
  const initialPaymentType =
    defaultPaymentType && paymentTypes.includes(defaultPaymentType)
      ? defaultPaymentType
      : paymentTypes[0];
  const [{ paymentType: selectedPaymentType }, setUrlState] = useUrlState({
    paymentType: initialPaymentType,
  });

  const handlePaymentChange = (type: CollectMethodPaymentType) => {
    callIfFunction(onTabClick, selectedPaymentType, type);
    setUrlState({ paymentType: type });
    // TODO: Reset error state on payment type change
  };

  // No types, no collect methods
  if (paymentTypes.length === 0) {
    return (
      <ErrorMessage>
        {t('data.payment_method.missing_payment_method')}
      </ErrorMessage>
    );
  }

  // Ugly way of passing props only to the Forms that support them
  const extraProps = selectedPaymentType === 'card' ? { isUniquePayment } : {};
  const [currentCM] = groupedCollectMethods[selectedPaymentType];
  const showSetAsDefaultPM = currentCM ? planCM.includes(currentCM.id) : false;

  return (
    <>
      <TabGroup>
        {paymentTypes.map((type) => {
          return (
            <Tab
              key={type}
              active={selectedPaymentType === type}
              onClick={() => handlePaymentChange(type)}
            >
              {t(`data.payment.methods.${type}`)}
            </Tab>
          );
        })}
      </TabGroup>
      {/* {paymentTypes.map((paymentType) => {
        const formClass = selectedPaymentType === paymentType ? 'flex' : 'none';
        // Ugly way of passing props only to the Forms that support them
        const extraProps = paymentType === 'card' ? { isUniquePayment } : {};
        const [currentCM] = groupedCollectMethods[paymentType];
        const showSetAsDefaultPM = currentCM
          ? planCM.includes(currentCM.id)
          : false;

        return currentCM ? (

        ) : null;
      })} */}
      <Row
        id={selectedPaymentType}
        gap="2rem"
        margin="0"
        alignItems="flex-start"
        key={selectedPaymentType}
      >
        <PaymentMethodForm
          paymentCategory={selectedPaymentType}
          collectMethods={currentCM}
          pagoticId={pagoticId ?? ''}
          onSubmit={onNext}
          onCancel={onCancel}
          processing={processing}
          submitText={submitText}
          cancelText={cancelText}
          country={country}
          termsAndConditionsUrl={brandingSettings?.terms_and_conditions}
          errorObj={errorObj}
          showSetAsDefaultPM={showSetAsDefaultPM}
          {...extraProps}
        />
      </Row>
      {groupedCollectMethods[selectedPaymentType][0].type !== 'pagotic' && (
        <CardProcessorLogo
          collectMethod={
            groupedCollectMethods
              ? groupedCollectMethods[selectedPaymentType][0]
              : undefined
          }
        />
      )}
    </>
  );
}
